import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import insights from "@/data/insightsData";

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function Insights() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, margin: "-40px" });

  return (
    <div className="min-h-screen bg-white" data-testid="insights-page">
      <Navbar />

      {/* HERO + CARDS — continuous background */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-32 md:pt-40 pb-[30px] md:pb-3"
        data-testid="insights-hero"
        style={{
          background:
            "linear-gradient(180deg, #f8faff 0%, #f3f5fb 50%, #f8faff 100%)",
        }}
      >
        <div className="absolute inset-0 grid-pattern opacity-[0.06]" />

        <motion.div
          className="absolute pointer-events-none"
          style={{ top: "5%", left: "-8%" }}
          animate={{ x: [0, 10, 0], y: [0, -6, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        >
          <div
            className="w-[500px] h-[600px] md:w-[600px] md:h-[700px] rounded-full blur-[140px]"
            style={{
              background:
                "radial-gradient(circle, rgba(79,107,255,0.12) 0%, rgba(79,107,255,0.04) 50%, transparent 75%)",
            }}
          />
        </motion.div>

        <motion.div
          className="absolute pointer-events-none"
          style={{ bottom: "-10%", right: "-6%" }}
          animate={{ x: [0, -8, 0], y: [0, 10, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        >
          <div
            className="w-[400px] h-[500px] md:w-[500px] md:h-[600px] rounded-full blur-[140px]"
            style={{
              background:
                "radial-gradient(circle, rgba(123,97,255,0.10) 0%, rgba(79,107,255,0.04) 50%, transparent 75%)",
            }}
          />
        </motion.div>

        <div
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{
            background: "linear-gradient(to bottom, transparent, #ffffff)",
          }}
        />

        {/* Hero text */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="flex items-center justify-center gap-3 mb-8"
          >
            <div className="w-8 h-px bg-[#4F6BFF]" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#4F6BFF]/80">
              Perspectives
            </span>
            <div className="w-8 h-px bg-[#4F6BFF]" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{
              duration: 0.65,
              delay: 0.1,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] mb-6"
            data-testid="insights-heading"
          >
            Insights
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.22 }}
            className="font-body text-[15px] md:text-base leading-[1.75] text-[#475569] max-w-[680px] mx-auto"
            data-testid="insights-intro"
          >
            Research, perspectives, and market analysis from the Nebula Flow team. We share our thinking
            <br className="hidden md:block" />
            on the industries, technologies, and themes that shape our investment approach.
          </motion.p>
        </div>

        {/* Cards grid */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 mt-12 md:mt-16 pb-4">
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="insights-grid"
          >
            {insights.map((insight, i) => (
              <Reveal key={insight.slug} delay={i * 0.06}>
                <Link
                  to={`/insights/${insight.slug}`}
                  data-testid={`insight-card-${insight.slug}`}
                  className="group block h-full"
                >
                  <div className="h-full rounded-2xl overflow-hidden border border-black/[0.04] card-gradient-line bg-white transition-all duration-300 hover:-translate-y-[2px] portfolio-card">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={insight.image}
                        alt={insight.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
                    </div>

                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="font-body text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4F6BFF]">
                          {insight.category}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                        <span className="font-body text-[12px] text-[#94a3b8]">
                          {insight.date}
                        </span>
                      </div>

                      <h3 className="font-body text-[17px] font-semibold text-[#08122a] tracking-tight leading-[1.35] mb-3 group-hover:text-[#4F6BFF] transition-colors duration-200">
                        {insight.title}
                      </h3>

                      <p className="font-body text-[14px] leading-[1.7] text-[#475569] mb-5 line-clamp-3">
                        {insight.excerpt}
                      </p>

                      <div className="flex items-center gap-2 font-body text-[13px] font-medium text-[#08122a] group-hover:text-[#4F6BFF] transition-colors duration-200">
                        Read more
                        <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
